import api from '@/services/api';
import { ContentFilters } from './types';

export interface ListeningContent {
    id: string;
    title: string;
    content_type: string;
    difficulty: string;
    cefr_level: string;
    audio_url: string;
    audio_duration: number;
    speaking_speed_wpm: number;
    transcript: string;
    description: string;
    questions: any[];
}

export interface ListeningSession {
    id: string;
    content: ListeningContent;
    current_segment: number;
    total_segments: number;
    overall_listening_score: number;
    comprehension_score: number;
    created_at: string;
}

const listeningService = {
    getListeningContents: async (filters?: ContentFilters) => {
        const params = new URLSearchParams();

        if (filters) {
            Object.entries(filters).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== '') {
                    params.append(key, value.toString());
                }
            });
        }
        const response = await api.get(`/listening/contents/?${params}`);
        return response.data;
    },

    // Content Management
    getListeningContent: async (id: string) => {
        const response = await api.get(`/listening/contents/${id}/`);
        return response.data;
    },

    getListeningContentByPrimaryId: async (primary_content_id: string) => {
        const response = await api.get(`/listening/contents/primary/${primary_content_id}/`);
        return response.data;
    },

    // Session Management
    startListeningSession: async (contentId: string) => {
        const response = await api.post('/listening/sessions/', { content_id: contentId });
        return response.data;
    },

    createListeningSession: async (contentId: string, config: any = {}) => {
        const payload = {
            content_id: contentId,
            base_session_id: config.base_session_id,
            feedback_tone: config.feedback_tone || 'encouraging',
            enable_realtime_feedback: config.enable_realtime_feedback !== false,
            enable_transcript: config.enable_transcript || false,
            enable_avatar_mode: config.enable_avatar_mode || false,
            enable_repeat_audio: config.enable_repeat_audio !== false,
        };
        const response = await api.post('/listening/sessions/create/', payload);
        return response.data;
    },

    completeListeningSession: async (sessionId: string, answers: any[] = []) => {
        const payload = {
            answers: answers,
            performance_data: {},
        };
        const response = await api.post(`/listening/sessions/${sessionId}/complete/`, payload);
        return response.data;
    },

    getListeningSession: async (sessionId: string) => {
        const response = await api.get(`/listening/sessions/${sessionId}/`);
        return response.data;
    },

    submitListeningAnswer: async (sessionId: string, data: any) => {
        const response = await api.post(`/listening/sessions/${sessionId}/submit/`, data);
        return response.data;
    },

    // Audio Processing
    uploadAudio: async (sessionId: string, audioBlob: Blob) => {
        const formData = new FormData();
        formData.append('audio', audioBlob);
        const response = await api.post(`/listening/sessions/${sessionId}/audio/`, formData);
        return response.data;
    },

    // Feedback and Analysis
    getSessionFeedback: async (sessionId: string) => {
        const response = await api.get(`/listening/sessions/${sessionId}/feedback/`);
        return response.data;
    },

    generateFeedback: async (sessionId: string) => {
        const response = await api.post(`/listening/sessions/${sessionId}/generate-feedback/`);
        return response.data;
    },
};

export default listeningService;
